import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    model,
    output,
    signal,
    untracked,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25EmailRecipientsComponent } from "../../../s25-email/s25.email.recipients.component";
import { S25RichTextEditorModule } from "../../../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";
import { S25LoadingInlineStaticComponent } from "../../../s25-loading/s25.loading.inline.static.component";
import { FormAssignmentRecipient } from "../../../data-ingestion/data.ingestion.service";
import { ContactRole } from "../../../../services/organization.service";
import { EmailService } from "../../../../services/email.service";
import { NotificationService } from "../../../../services/notification.service";
import { EmailTemplate } from "../../../bpe/bpe.service";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { Report } from "../../../../pojo/Report";
import { S25Util } from "../../../../util/s25-util";
import { PaymentEmailBody } from "./payment-email-body";
import { PaymentEmailTemplate } from "./payment-email-template";
import { S25CheckboxModule } from "../../../s25-checkbox/s25.checkbox.module";
import { S25TriggerRerenderDirective } from "../../../s25-trigger-rerender/s25.trigger.rerender.directive";

export type PaymentDetailsOption = { label: string; body: string };

export type PaymentDetails = PaymentDetailsOption & { withoutLink?: PaymentDetailsOption };

export type PaymentEmailPayload = {
    subject: string;
    body: string;
    to: string;
    cc: string;
    bcc: string;
    from: string;
    reports: Report.SimpleObject[];
    /** The manual template the message came from, so the send can be recorded against it. */
    templateId: number | undefined;
};

/**
 * The payment request's own compose form. It mirrors `s25-ng-email-event`'s shape - a manual BPE
 * template fills the message, and reports ride along as attachments - but owns the From address,
 * and its recipients are organization contacts rather than event roles.
 *
 * Everything the form shows is seeded by the caller and re-seeded whenever `seedKey` changes.
 */
@TypeManagerDecorator("s25-ng-payment-email")
@Component({
    selector: "s25-ng-payment-email",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        S25EmailRecipientsComponent,
        S25RichTextEditorModule,
        S25ButtonComponent,
        S25DropdownPaginatedModule,
        S25LoadingInlineStaticComponent,
        S25CheckboxModule,
        S25TriggerRerenderDirective,
    ],
    template: `
        <div class="email-header">
            <h3>Compose Email</h3>
            <div class="header-right">
                @if (!showCc()) {
                    <button type="button" class="toggle-link" (click)="showCc.set(true)">+ CC</button>
                }
                @if (!showBcc()) {
                    <button type="button" class="toggle-link" (click)="showBcc.set(true)">+ BCC</button>
                }
            </div>
        </div>

        @if (templates().length) {
            <div class="template-picker">
                @if (profiles().length > 1) {
                    <label class="field field--narrow">
                        <strong>Segment</strong>
                        <select
                            class="input-field"
                            [ngModel]="profileId()"
                            (ngModelChange)="profileId.set(+$event)"
                            [disabled]="busy()"
                        >
                            <option [ngValue]="allProfilesId">All Segments</option>
                            @for (profile of profiles(); track profile.profile_id) {
                                <option [ngValue]="profile.profile_id">{{ profile.profile_name }}</option>
                            }
                        </select>
                    </label>
                }

                <label class="field field--narrow">
                    <strong>Email Template</strong>
                    <select
                        class="input-field"
                        [ngModel]="templateId()"
                        (ngModelChange)="templateId.set(+$event)"
                        [disabled]="busy()"
                    >
                        <option [ngValue]="0">No Template</option>
                        @for (template of templates(); track template.itemId) {
                            <option [ngValue]="template.itemId">{{ template.itemName }}</option>
                        }
                    </select>
                </label>

                @if (templateLoading()) {
                    <s25-ng-loading-inline-static />
                }
            </div>
        }

        <s25-ng-email-recipients
            [label]="'TO:'"
            [(recipients)]="toRecipients"
            [(draftEmail)]="toDraft"
            [orgMembers]="orgMembers()"
            [memberLabel]="'Contacts'"
            [disabled]="busy()"
        />

        @if (showCc()) {
            <s25-ng-email-recipients
                [label]="'CC:'"
                [(recipients)]="ccRecipients"
                [(draftEmail)]="ccDraft"
                [orgMembers]="orgMembers()"
                [memberLabel]="'Contacts'"
                [disabled]="busy()"
            />
        }

        @if (showBcc()) {
            <s25-ng-email-recipients
                [label]="'BCC:'"
                [(recipients)]="bccRecipients"
                [(draftEmail)]="bccDraft"
                [orgMembers]="orgMembers()"
                [memberLabel]="'Contacts'"
                [disabled]="busy()"
            />
        }

        <s25-ng-email-recipients
            [label]="'FROM:'"
            [(recipients)]="fromRecipients"
            [(draftEmail)]="fromDraft"
            [orgMembers]="orgMembers()"
            [memberLabel]="'Contacts'"
            [disabled]="busy()"
        />

        <label class="field">
            <strong>Subject <span aria-hidden="true">*</span></strong>
            <input
                class="input-field"
                type="text"
                [ngModel]="subject()"
                (ngModelChange)="subject.set($event)"
                [disabled]="busy()"
            />
        </label>

        <div class="field field--narrow">
            <strong>Attachments</strong>
            <s25-ng-generic-multiselect-dropdown
                *s25-ng-trigger-rerender="seedKey()"
                [items]="reportItems()"
                [value]="selectedReportItems()"
                [hasSelectAll]="true"
                [placeholder]="'No attachments'"
                [ariaLabel]="'Attachments'"
                (done)="onReportsChange($event)"
            />
        </div>

        <div class="field">
            <div class="body-label">
                <strong>Message</strong>
                <button type="button" class="toggle-link" (click)="resetForm()" [disabled]="busy()">
                    Reset to default
                </button>
            </div>
            <p class="token-help">{{ tokenHelp() }}</p>
            @if (templateId() && detailsOption(); as details) {
                <s25-ng-checkbox
                    class="details-toggle"
                    [value]="includesPaymentDetails()"
                    (valueChange)="togglePaymentDetails($event)"
                    [disabled]="busy()"
                    >{{ details.label }}</s25-ng-checkbox
                >
            }
            <s25-ng-rich-text-editor
                [modelValue]="body()"
                (modelValueChange)="body.set($event)"
                [ariaLabel]="'Message'"
                [autoResize]="true"
            />
        </div>

        <div class="button-group">
            <s25-ng-button [type]="'outline'" [disabled]="sending()" (clicked)="closed.emit()">Cancel</s25-ng-button>
            <s25-ng-button [type]="'primary'" [disabled]="sending()" (clicked)="send()">
                {{ sending() ? "Sending..." : "Send Email" }}
            </s25-ng-button>
        </div>
    `,
    styles: `
        :host {
            display: block;
        }

        .email-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1rem;
            padding-bottom: 0.75rem;
            border-bottom: 2px solid var(--color--border);
        }

        .email-header h3 {
            margin: 0;
            font-size: 1.25rem;
        }

        .header-right {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .toggle-link {
            background: none;
            border: none;
            color: var(--color--link-text);
            font-size: 13px;
            cursor: pointer;
            padding: 2px 6px;
            border-radius: 3px;
        }

        .toggle-link:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        .field {
            display: block;
            margin-bottom: 1rem;
        }

        .field--narrow {
            max-width: 24em;
        }

        .template-picker {
            display: flex;
            align-items: flex-end;
            gap: 1rem;
            flex-wrap: wrap;
        }

        .template-picker .field {
            flex: 1 1 14em;
        }

        .input-field {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
            padding: 10px;
            margin-top: 5px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .body-label {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.5rem;
        }

        .token-help {
            margin: 0 0 0.5rem;
            color: var(--color--text-muted);
            font-size: 0.85rem;
        }

        .details-toggle {
            display: block;
            margin-bottom: 0.5rem;
        }

        .button-group {
            display: flex;
            gap: 10px;
            justify-content: flex-end;
        }
    `,
})
export class S25PaymentEmailComponent {
    readonly orgMembers = input<ContactRole[]>([]);
    readonly defaultToRecipients = input<FormAssignmentRecipient[]>([]);
    readonly defaultCcRecipients = input<FormAssignmentRecipient[]>([]);
    readonly defaultBccRecipients = input<FormAssignmentRecipient[]>([]);
    readonly defaultFrom = input("");
    readonly defaultSubject = input("");
    readonly defaultBody = input("");
    readonly reports = input<Report.SimpleObject[]>([]);
    readonly defaultReportIds = input<number[]>([]);
    readonly templates = input<EmailTemplate[]>([]);
    readonly profiles = input<PaymentEmailTemplate.EventProfile[]>([]);
    /**
     * What the processor needs the payer told, and how to describe it on the checkbox. Undefined
     * for a processor that needs nothing, which hides the checkbox.
     */
    readonly paymentDetails = input<PaymentDetails | undefined>(undefined);
    /** True while the caller is resolving the template the author just picked. */
    readonly templateLoading = input(false);
    /**
     * Changed by the caller each time the form is opened, and each time a template is picked.
     * Everything below re-seeds from it and from nothing else, so editing the amount mid-compose
     * cannot wipe out a rewritten message.
     */
    readonly seedKey = input<string | number>(0);

    /** Which template and segment the caller should resolve. Zero means no template. */
    readonly templateId = model(0);
    readonly profileId = model(PaymentEmailTemplate.ALL_PROFILES_ID);

    readonly sendHandler = input.required<(payload: PaymentEmailPayload) => Promise<{ error?: string }>>();
    readonly closed = output<void>();

    /** Payment details are only handed over for a processor with a portal to link to. */
    protected readonly tokenHelp = computed(() => PaymentEmailBody.paymentTokenHelp(!!this.paymentDetails()));
    protected readonly allProfilesId = PaymentEmailTemplate.ALL_PROFILES_ID;

    protected showCc = linkedSignal(() => this.defaultCcRecipients().length > 0);
    protected showBcc = linkedSignal(() => this.defaultBccRecipients().length > 0);
    protected sending = signal(false);

    /** Nothing may be edited while a template is resolving or the message is on its way out. */
    protected readonly busy = computed(() => this.sending() || this.templateLoading());

    protected fromRecipients = linkedSignal<string | number, FormAssignmentRecipient[]>({
        source: this.seedKey,
        computation: () => untracked(() => (this.defaultFrom() ? [{ email: this.defaultFrom() }] : [])),
    });
    protected toRecipients = linkedSignal<string | number, FormAssignmentRecipient[]>({
        source: this.seedKey,
        computation: () => untracked(() => this.defaultToRecipients()),
    });
    protected ccRecipients = linkedSignal<string | number, FormAssignmentRecipient[]>({
        source: this.seedKey,
        computation: () => untracked(() => this.defaultCcRecipients()),
    });
    protected bccRecipients = linkedSignal<string | number, FormAssignmentRecipient[]>({
        source: this.seedKey,
        computation: () => untracked(() => this.defaultBccRecipients()),
    });
    protected fromDraft = signal("");
    protected toDraft = signal("");
    protected ccDraft = signal("");
    protected bccDraft = signal("");

    protected reportIds = linkedSignal<string | number, number[]>({
        source: this.seedKey,
        computation: () => untracked(() => this.defaultReportIds()),
    });
    protected subject = linkedSignal<string | number, string>({
        source: this.seedKey,
        computation: () => untracked(() => this.defaultSubject()),
    });
    /**
     * The amount and the processor are chosen in the same sidebar, after this form is on screen, and
     * each changes what the default message says - a 7 Point request has to quote an identifier, for
     * one. So a message the author has not touched follows the default, while a rewritten one stands.
     */
    protected body = linkedSignal<{ key: string | number; default: string }, string>({
        source: () => ({ key: this.seedKey(), default: this.defaultBody() }),
        computation: (source, previous) => {
            if (!previous || previous.source.key !== source.key) return source.default;
            const wasEdited = previous.value !== previous.source.default;
            return wasEdited ? previous.value : source.default;
        },
    });

    /**
     * Read from the message rather than held alongside it, so deleting the block by hand unticks
     * the box and the two can never disagree.
     */
    protected readonly includesPaymentDetails = computed(() => {
        const details = this.detailsOption();
        return !!details && this.body().includes(details.body);
    });

    /** A link the template or the author wrote themselves, outside the block this form adds. */
    private readonly authoredLink = computed(() => {
        const details = this.paymentDetails();
        const body = details ? this.body().replace(details.body, "") : this.body();
        return PaymentEmailBody.usesPaymentLink(body);
    });

    /**
     * Only one link in a message is made live, so once the message has its own, the checkbox stops
     * offering another. A block already in the message stays on offer so it can be taken back out.
     */
    protected readonly detailsOption = computed<PaymentDetailsOption | undefined>(() => {
        const details = this.paymentDetails();
        if (!details) return undefined;
        if (this.body().includes(details.body)) return details;
        return this.authoredLink() ? details.withoutLink : details;
    });

    protected togglePaymentDetails(include: boolean) {
        const details = this.detailsOption();
        if (!details) return;

        const current = this.body();
        if (!include) return this.body.set(current.replace(details.body, ""));
        if (!current.includes(details.body)) this.body.set(current + details.body);
    }

    protected readonly reportItems = computed<DropDownItem[]>(() =>
        this.reports().map((report) => ({
            val: Number(report.rpt_id),
            itemId: Number(report.rpt_id),
            txt: report.rpt_name,
            itemName: report.rpt_name,
        })),
    );

    protected readonly selectedReportItems = computed<DropDownItem[]>(() => {
        const chosen = new Set(this.reportIds());
        return this.reportItems().filter((item) => chosen.has(Number(item.val)));
    });

    private readonly selectedReports = computed(() => {
        const chosen = new Set(this.reportIds());
        return this.reports().filter((report) => chosen.has(Number(report.rpt_id)));
    });

    protected onReportsChange(items: DropDownItem[]) {
        this.reportIds.set((items ?? []).map((item) => Number(item.val ?? item.itemId)));
    }

    protected resetForm() {
        this.templateId.set(0);
        this.body.set(this.defaultBody());
    }

    protected async send() {
        if (this.sending()) return;

        const to = this.recipientRefs(this.toRecipients(), this.toDraft());
        if (!to) return NotificationService.post("Please choose at least one recipient");
        if (!this.subject().trim()) return NotificationService.post("Please enter a subject");
        if (!this.body().trim()) return NotificationService.post("Please enter a message");

        const senders = this.fromAddresses();
        if (!senders.length) return NotificationService.post("Please choose a From address");
        if (senders.length > 1) return NotificationService.post("A message can only have one From address");
        if (!S25Util.isValidEmail(senders[0])) return NotificationService.post("Enter a valid From address");

        this.sending.set(true);

        const sendPromise = this.sendHandler()({
            subject: this.subject(),
            body: this.body(),
            to,
            cc: this.recipientRefs(this.ccRecipients(), this.ccDraft()),
            bcc: this.recipientRefs(this.bccRecipients(), this.bccDraft()),
            from: senders[0],
            reports: this.selectedReports(),
            templateId: this.templateId() || undefined,
        })
            .then((result) => {
                this.sending.set(false);
                if (result?.error) return NotificationService.post("Email(s) failed: " + result.error);

                NotificationService.post("Email(s) have been successfully sent");
                this.closed.emit();
            })
            .catch((error: unknown) => {
                this.sending.set(false);
                NotificationService.post("Email(s) failed: " + S25Util.errorText(error, "Uncaught Error"));
            });

        NotificationService.post("Email(s) are sending...", sendPromise);
    }

    /** An address typed but never committed with Enter still counts as a recipient. */
    private recipientRefs(recipients: FormAssignmentRecipient[], draft: string): string {
        return EmailService.joinRecipientRefs([...recipients, draft.trim()].filter(Boolean));
    }

    /**
     * The From row is read for addresses rather than joined like the others: a sender has to be a
     * real address, where `joinRecipientRefs` would hand back a contact id.
     */
    private fromAddresses(): string[] {
        return [...this.fromRecipients().map((recipient) => recipient.email), this.fromDraft().trim()]
            .map((address) => address?.trim())
            .filter((address): address is string => !!address);
    }
}
